<template>
    <a-modal
        v-model:open="localVisible"
        :title="modalTitle"
        :width="mode === 'condition' ? 800 : 900"
        :z-index="3000"
        :mask-z-index="2999"
        autocomplete="off"
        data-lpignore="true"
        data-form-type="other"
        @cancel="handleCancel"
    >
        <div class="node-selector-tip">
            <a-alert
                :message="tipMessage"
                type="info"
                show-icon
                :closable="false"
            />
        </div>
        <template #footer>
            <div
                v-if="mode !== 'condition'"
                class="chapter-node-selector-footer"
            >
                <!-- 已選擇的節點顯示區域（多選模式） -->
                <div
                    v-if="multiple && selectedNodes.length > 0"
                    class="selected-nodes-display"
                >
                    <div class="selected-nodes-header">
                        <span class="selected-count">
                            已選擇 {{ selectedNodes.length }} 個節點
                        </span>
                    </div>
                    <div class="selected-nodes-tags">
                        <a-tag
                            v-for="(node, index) in selectedNodes"
                            :key="index"
                            closable
                            @close="removeSelectedNode(index)"
                            class="selected-node-tag"
                        >
                            {{ node.label }}
                        </a-tag>
                    </div>
                </div>
                <!-- 按鈕區域 -->
                <div class="footer-actions">
                    <a-button @click="handleCancel">取消</a-button>
                    <a-button
                        v-if="multiple"
                        type="primary"
                        @click="handleAddToSelection"
                    >
                        加入選擇
                    </a-button>
                    <a-button
                        v-if="multiple && selectedNodes.length > 0"
                        type="primary"
                        @click="handleFinishMultiple"
                    >
                        完成選擇
                    </a-button>
                    <a-button
                        v-else-if="!multiple"
                        type="primary"
                        @click="handleConfirm"
                    >
                        確認
                    </a-button>
                </div>
            </div>
            <div v-else>
                <a-button @click="handleCancel">取消</a-button>
                <a-button
                    type="primary"
                    @click="handleConfirm"
                    style="margin-left: 8px"
                >
                    確認
                </a-button>
            </div>
        </template>
        <div
            class="node-selector"
            autocomplete="off"
            data-lpignore="true"
            data-form-type="other"
        >
            <a-row :gutter="16" align="bottom">
                <!-- 第一層：選擇 Mission -->
                <a-col :span="6">
                    <a-form-item label="主線" style="margin-bottom: 0">
                        <a-select
                            :dropdown-match-select-width="false"
                            dropdown-class-name="mission-node-selector-dropdown"
                            v-model:value="selectedNodePath.missionId"
                            placeholder="選擇主線"
                            style="width: 100%"
                            autocomplete="off"
                            show-search
                            option-filter-prop="label"
                            :options="missionSelectOptions"
                            list-height="240"
                            :virtual="false"
                            :get-popup-container="
                                (trigger) => trigger.parentNode
                            "
                            @change="handleMissionChange"
                        />
                    </a-form-item>
                </a-col>

                <!-- 第二層：選擇 Chapter -->
                <a-col :span="6">
                    <a-form-item label="章節" style="margin-bottom: 0">
                        <a-select
                            v-model:value="selectedNodePath.chapterId"
                            placeholder="選擇章節"
                            style="width: 100%"
                            :disabled="!selectedNodePath.missionId"
                            :loading="loadingChapters"
                            :key="`chapter-select-${selectedNodePath.missionId}`"
                            show-search
                            :filter-option="filterOptionByLabel"
                            :get-popup-container="
                                (trigger) => trigger.parentNode
                            "
                            @focus="handleChapterSelectFocus"
                        >
                            <a-select-option
                                v-for="chapter in availableChapters"
                                :key="chapter.value"
                                :value="chapter.value"
                            >
                                {{ chapter.label }}
                            </a-select-option>
                        </a-select>
                    </a-form-item>
                </a-col>

                <!-- 第三層：選擇 Task -->
                <a-col :span="6">
                    <a-form-item label="任務" style="margin-bottom: 0">
                        <a-select
                            v-model:value="selectedNodePath.taskId"
                            placeholder="選擇任務"
                            style="width: 100%"
                            :disabled="!selectedNodePath.chapterId"
                            :loading="loadingTasks"
                            show-search
                            autocomplete="off"
                            data-lpignore="true"
                            data-form-type="other"
                            :filter-option="filterOption"
                            :get-popup-container="
                                (trigger) => trigger.parentNode
                            "
                            @focus="handleTaskSelectFocus"
                        >
                            <a-select-option
                                v-for="task in availableTasks"
                                :key="normalizeNodeId(task.id)"
                                :value="normalizeNodeId(task.id)"
                            >
                                {{ task.name }}
                            </a-select-option>
                        </a-select>
                    </a-form-item>
                </a-col>

                <!-- 第四層：選擇 Step -->
                <a-col :span="6">
                    <a-form-item label="步驟" style="margin-bottom: 0">
                        <a-select
                            v-model:value="selectedNodePath.stepId"
                            placeholder="選擇步驟（選填）"
                            style="width: 100%"
                            :disabled="!selectedNodePath.taskId"
                            :loading="loadingSteps"
                            show-search
                            autocomplete="off"
                            data-lpignore="true"
                            data-form-type="other"
                            :filter-option="filterOption"
                            allow-clear
                            :get-popup-container="
                                (trigger) => trigger.parentNode
                            "
                            @focus="handleStepSelectFocus"
                        >
                            <a-select-option
                                v-for="step in availableSteps"
                                :key="normalizeNodeId(step.id)"
                                :value="normalizeNodeId(step.id)"
                            >
                                {{ step.name }}
                            </a-select-option>
                        </a-select>
                    </a-form-item>
                </a-col>
            </a-row>
            <!-- 顯示選擇的路徑 -->
            <div
                v-if="
                    selectedNodePath.missionId ||
                    selectedNodePath.chapterId ||
                    selectedNodePath.taskId ||
                    selectedNodePath.stepId
                "
                class="selected-path"
            >
                <a-divider />
                <div class="path-label">已選擇的路徑：</div>
                <div class="path-content">
                    <a-tag v-if="selectedMission" color="blue">
                        主線：{{ selectedMission.name }}
                    </a-tag>
                    <a-tag v-if="selectedChapter" color="green">
                        章節：{{ selectedChapter.name }}
                    </a-tag>
                    <a-tag v-if="selectedTask" color="orange">
                        任務：{{ selectedTask.name }}
                    </a-tag>
                    <a-tag v-if="selectedStep" color="purple">
                        步驟：{{ selectedStep.name }}
                    </a-tag>
                </div>
                <div
                    v-if="mode !== 'condition' && getNodeLabelFromPath()"
                    style="margin-top: 8px; font-weight: 600; color: #2a4a7a"
                >
                    主要節點：{{ getNodeLabelFromPath() }}
                </div>
            </div>
        </div>
    </a-modal>
</template>

<script setup>
import { ref, reactive, computed, watch, nextTick } from 'vue';
import { message } from 'ant-design-vue';
import { showErrorMessage } from '@/utils/messageHelper';

const props = defineProps({
    visible: {
        type: Boolean,
        default: false
    },
    mode: {
        type: String,
        default: 'condition', // 'condition' | 'unlock' | 'trigger'
        validator: (value) =>
            ['condition', 'unlock', 'trigger', 'unlock-source'].includes(value)
    },
    multiple: {
        type: Boolean,
        default: false
    },
    initialCode: {
        type: String,
        default: null
    },
    initialNodeId: {
        type: Number,
        default: null
    },
    initialNodeIds: {
        type: Array,
        default: () => []
    },
    missions: {
        type: Array,
        default: () => []
    },
    missionHierarchyCache: {
        type: Object,
        default: () => ({})
    },
    ensureMissionHierarchy: {
        type: Function,
        required: true
    },
    getMissionHierarchyList: {
        type: Function,
        required: true
    },
    forceLoadTaskSteps: {
        type: Function,
        default: null
    },
    getNodeHierarchy: {
        type: Function,
        default: null
    }
});

const emit = defineEmits(['update:visible', 'confirm']);

// 本地 visible 狀態
const localVisible = computed({
    get: () => props.visible,
    set: (value) => emit('update:visible', value)
});

// 選擇的路徑
const selectedNodePath = reactive({
    missionId: null,
    chapterId: null,
    taskId: null,
    stepId: null
});

// 多選模式下已選擇的節點
const selectedNodes = ref([]);

// 載入狀態
const loadingChapters = ref(false);
const loadingTasks = ref(false);
const loadingSteps = ref(false);

// 保存從 API 載入的初始節點名稱（用於單選模式）
const initialNodeName = ref(null);

// 正規化節點 ID
const normalizeNodeId = (value) => {
    if (value === undefined || value === null || value === '') return null;
    return String(value);
};

// Modal 標題
const modalTitle = computed(() => {
    if (props.mode === 'condition') {
        return '選擇任務節點';
    }
    if (props.mode === 'unlock' || props.mode === 'unlock-source') {
        return '選擇前置任務（單選）';
    }
    return props.multiple ? '選擇觸發任務（可多選）' : '選擇觸發任務（單選）';
});

// 提示訊息
const tipMessage = computed(() => {
    if (props.mode === 'condition') {
        return '提示：您可以選擇任意層級的節點作為條件判斷。選擇任務層級時，完成該任務即滿足條件；選擇主線、章節、任務或步驟時，完成對應層級即滿足條件。';
    }
    if (props.mode === 'unlock' || props.mode === 'unlock-source') {
        return '選擇此任務的前置任務（單選）';
    }
    return props.multiple
        ? '選擇完成此任務後，會觸發哪些任務開始執行（可多選）'
        : '選擇觸發此任務的任務（單選）';
});

// 主線選項
const missionSelectOptions = computed(() =>
    (props.missions || []).map((mission) => ({
        label: mission.name || `未命名主線（ID: ${mission.id}）`,
        value: normalizeNodeId(mission.id)
    }))
);

// 取得選中的 Mission 物件
const selectedMission = computed(() => {
    const missionId = normalizeNodeId(selectedNodePath.missionId);
    if (missionId === null) return null;

    const list = props.getMissionHierarchyList() || [];

    return list.find((m) => normalizeNodeId(m.id) === missionId) || null;
});

// 取得選中的 Chapter 物件
const selectedChapter = computed(() => {
    const mission = selectedMission.value;
    const chapterId = normalizeNodeId(selectedNodePath.chapterId);
    if (!mission || chapterId === null) return null;
    return (
        (mission.chapters || []).find(
            (c) => normalizeNodeId(c.id) === chapterId
        ) || null
    );
});

// 取得選中的 Task 物件
const selectedTask = computed(() => {
    const chapter = selectedChapter.value;
    const taskId = normalizeNodeId(selectedNodePath.taskId);
    if (!chapter || taskId === null) return null;
    return (
        (chapter.tasks || []).find((t) => normalizeNodeId(t.id) === taskId) ||
        null
    );
});

// 取得選中的 Step 物件
const selectedStep = computed(() => {
    const task = selectedTask.value;
    const stepId = normalizeNodeId(selectedNodePath.stepId);
    if (!task || stepId === null) return null;
    return (
        (task.steps || []).find((s) => normalizeNodeId(s.id) === stepId) || null
    );
});

// 從 hierarchy list 取得 Mission
const getMissionFromHierarchyCache = (missionId) => {
    const normalizedId = normalizeNodeId(missionId);
    if (normalizedId === null) return null;

    const list = props.getMissionHierarchyList() || [];

    return list.find((m) => normalizeNodeId(m.id) === normalizedId) || null;
};

// 可用的章節列表
const availableChapters = computed(() => {
    const missionId = normalizeNodeId(selectedNodePath.missionId);
    if (missionId === null) return [];

    const list = props.getMissionHierarchyList() || [];

    const mission =
        list.find((m) => normalizeNodeId(m.id) === missionId) || null;

    if (!mission || !Array.isArray(mission.chapters)) return [];

    return mission.chapters.map((ch) => ({
        label: ch.name || `未命名章節（ID: ${ch.id}）`,
        value: normalizeNodeId(ch.id)
    }));
});

// 可用的任務列表
const availableTasks = computed(() => {
    if (!selectedChapter.value) return [];
    return selectedChapter.value.tasks || [];
});

// 可用的步驟列表
const availableSteps = computed(() => {
    if (!selectedTask.value) return [];
    return selectedTask.value.steps || [];
});

// 主線變更處理
const handleMissionChange = async (missionId) => {
    const normalizedId = normalizeNodeId(missionId);
    if (normalizedId !== null) {
        // 先載入資料，再更新 selectedNodePath
        loadingChapters.value = true;
        try {
            await props.ensureMissionHierarchy(normalizedId);
            // 等待資料更新
            await nextTick();
        } finally {
            loadingChapters.value = false;
        }
    }
    selectedNodePath.missionId = normalizeNodeId(missionId);
    selectedNodePath.chapterId = null;
    selectedNodePath.taskId = null;
    selectedNodePath.stepId = null;
    // 清空初始節點名稱（因為用戶選擇了新節點）
    initialNodeName.value = null;
};

// 章節下拉選單 focus 時，確保資料已載入
const handleChapterSelectFocus = async () => {
    const missionId = normalizeNodeId(selectedNodePath.missionId);
    if (missionId !== null) {
        const mission = props
            .getMissionHierarchyList()
            .find((m) => normalizeNodeId(m.id) === missionId);
        // 如果 mission 存在但 chapters 還沒載入（undefined 或 null），先載入
        // 或者如果 mission 不存在，也要嘗試載入（可能是資料還沒載入到 cache）
        if (!mission || !Array.isArray(mission.chapters)) {
            loadingChapters.value = true;
            try {
                await props.ensureMissionHierarchy(missionId);
                await nextTick();
            } finally {
                loadingChapters.value = false;
            }
        }
    }
};

// 任務下拉選單 focus 時，確保資料已載入
const handleTaskSelectFocus = async () => {
    const chapterId = normalizeNodeId(selectedNodePath.chapterId);
    const missionId = normalizeNodeId(selectedNodePath.missionId);
    if (chapterId !== null && missionId !== null) {
        loadingTasks.value = true;
        try {
            // 先確保主線階層已載入（這會載入章節的任務）
            await props.ensureMissionHierarchy(missionId);
            await nextTick();

            // 重新取得 mission 和 chapter 資料，確保取得最新資料
            const mission = props
                .getMissionHierarchyList()
                .find((m) => normalizeNodeId(m.id) === missionId);
            if (mission) {
                const chapter = (mission.chapters || []).find(
                    (ch) => normalizeNodeId(ch.id) === chapterId
                );
                // 如果 chapter 存在但 tasks 不是陣列，再等待一下確保資料已更新
                if (chapter && !Array.isArray(chapter.tasks)) {
                    await nextTick();
                }
            }
        } finally {
            loadingTasks.value = false;
        }
    }
};

// 步驟下拉選單 focus 時，確保資料已載入
const handleStepSelectFocus = async () => {
    const taskId = normalizeNodeId(selectedNodePath.taskId);
    const missionId = normalizeNodeId(selectedNodePath.missionId);
    if (taskId !== null && missionId !== null) {
        loadingSteps.value = true;
        try {
            // 先確保主線階層已載入（這會載入任務的 steps）
            await props.ensureMissionHierarchy(missionId);
            await nextTick();

            // 重新取得 mission、chapter 和 task 資料，確保取得最新資料
            const mission = props
                .getMissionHierarchyList()
                .find((m) => normalizeNodeId(m.id) === missionId);
            if (mission) {
                const chapter = (mission.chapters || []).find(
                    (ch) =>
                        normalizeNodeId(ch.id) ===
                        normalizeNodeId(selectedNodePath.chapterId)
                );
                if (chapter) {
                    const task = (chapter.tasks || []).find(
                        (t) => normalizeNodeId(t.id) === taskId
                    );
                    // 如果 task 存在但 steps 不是陣列，或 steps 為空但應該有資料，強制載入
                    if (
                        task &&
                        (!Array.isArray(task.steps) ||
                            (task.steps.length === 0 &&
                                props.forceLoadTaskSteps))
                    ) {
                        if (props.forceLoadTaskSteps) {
                            await props.forceLoadTaskSteps(taskId);
                            await nextTick();
                        }
                    }
                }
            }
        } finally {
            loadingSteps.value = false;
        }
    }
};

// 過濾選項
const filterOption = (input, option) => {
    return (
        option.children?.toLowerCase().includes(input.toLowerCase()) || false
    );
};

const filterOptionByLabel = (input, option) => {
    const text = String(option.label ?? option.children ?? '').toLowerCase();
    return text.includes(input.toLowerCase());
};

// 從路徑取得節點標籤
const getNodeLabelFromPath = () => {
    // 如果有保存的初始節點名稱，優先使用（用於單選模式，當節點還沒展開時）
    if (initialNodeName.value) {
        return initialNodeName.value;
    }

    if (selectedNodePath.stepId && selectedStep.value) {
        return selectedStep.value.name;
    } else if (selectedNodePath.taskId && selectedTask.value) {
        return selectedTask.value.name;
    } else if (selectedNodePath.chapterId && selectedChapter.value) {
        return selectedChapter.value.name;
    } else if (selectedNodePath.missionId && selectedMission.value) {
        return selectedMission.value.name;
    }
    return '';
};

// 從路徑生成節點 code
const getNodeCodeFromPath = () => {
    if (selectedNodePath.stepId) {
        // 確保使用數字 ID（字串 ID 轉數字）
        const stepId = Number(selectedNodePath.stepId);
        return stepId ? `step_${stepId}` : null;
    } else if (selectedNodePath.taskId) {
        const taskId = Number(selectedNodePath.taskId);
        return taskId ? `task_${taskId}` : null;
    } else if (selectedNodePath.chapterId) {
        const chapterId = Number(selectedNodePath.chapterId);
        return chapterId ? `chapter_${chapterId}` : null;
    } else if (selectedNodePath.missionId) {
        const missionId = Number(selectedNodePath.missionId);
        return missionId ? `mission_${missionId}` : null;
    }
    return null;
};

// 從 code 取得節點 ID
const getNodeIdFromCode = (code) => {
    if (!code) return null;
    const parts = code.split('_');
    if (parts.length < 2) return null;
    return Number(parts.slice(1).join('_'));
};

// 解析節點 code 為路徑
const parseNodeCodeToPath = async (code) => {
    if (!code) return;
    const parts = code.split('_');
    if (parts.length < 2) return;

    const nodeType = parts[0];
    const nodeId = Number(parts.slice(1).join('_'));
    const normalizedNodeId = normalizeNodeId(nodeId);

    for (const mission of props.getMissionHierarchyList()) {
        if (
            nodeType === 'mission' &&
            normalizeNodeId(mission.id) === normalizedNodeId
        ) {
            selectedNodePath.missionId = normalizeNodeId(mission.id);
            selectedNodePath.chapterId = null;
            selectedNodePath.taskId = null;
            selectedNodePath.stepId = null;
            return;
        }

        // 確保章節已載入
        if (!mission.chapters || mission.chapters.length === 0) {
            await props.ensureMissionHierarchy(normalizeNodeId(mission.id));
        }

        for (const chapter of mission.chapters || []) {
            if (
                nodeType === 'chapter' &&
                normalizeNodeId(chapter.id) === normalizedNodeId
            ) {
                selectedNodePath.missionId = normalizeNodeId(mission.id);
                selectedNodePath.chapterId = normalizeNodeId(chapter.id);
                selectedNodePath.taskId = null;
                selectedNodePath.stepId = null;
                return;
            }

            // 如果是 task 或 step 類型，確保該章節的任務已載入
            if (
                (nodeType === 'task' || nodeType === 'step') &&
                (!Array.isArray(chapter.tasks) || chapter.tasks.length === 0)
            ) {
                await props.ensureMissionHierarchy(normalizeNodeId(mission.id));
                // 重新取得 mission 資料
                const updatedMission = props
                    .getMissionHierarchyList()
                    .find(
                        (m) =>
                            normalizeNodeId(m.id) ===
                            normalizeNodeId(mission.id)
                    );
                if (updatedMission) {
                    const updatedChapter = (updatedMission.chapters || []).find(
                        (ch) =>
                            normalizeNodeId(ch.id) ===
                            normalizeNodeId(chapter.id)
                    );
                    if (updatedChapter && Array.isArray(updatedChapter.tasks)) {
                        // 使用更新後的 chapter 的 tasks
                        chapter.tasks = updatedChapter.tasks;
                    }
                }
            }

            for (const task of chapter.tasks || []) {
                if (
                    nodeType === 'task' &&
                    normalizeNodeId(task.id) === normalizedNodeId
                ) {
                    selectedNodePath.missionId = normalizeNodeId(mission.id);
                    selectedNodePath.chapterId = normalizeNodeId(chapter.id);
                    selectedNodePath.taskId = normalizeNodeId(task.id);
                    selectedNodePath.stepId = null;

                    // 確保該 task 的 steps 已載入（用於顯示下拉選單）
                    // 無論 steps 是否已載入，都確保完整階層已載入
                    await props.ensureMissionHierarchy(
                        normalizeNodeId(mission.id)
                    );
                    // 等待一下確保資料已更新到 cache
                    await nextTick();
                    // 重新從 getMissionHierarchyList 取得最新資料，確保 computed 能取得正確資料
                    const refreshedMission = props
                        .getMissionHierarchyList()
                        .find(
                            (m) =>
                                normalizeNodeId(m.id) ===
                                normalizeNodeId(mission.id)
                        );
                    if (refreshedMission) {
                        const refreshedChapter = (
                            refreshedMission.chapters || []
                        ).find(
                            (ch) =>
                                normalizeNodeId(ch.id) ===
                                normalizeNodeId(chapter.id)
                        );
                        if (refreshedChapter) {
                            const refreshedTask = (
                                refreshedChapter.tasks || []
                            ).find(
                                (t) =>
                                    normalizeNodeId(t.id) ===
                                    normalizeNodeId(task.id)
                            );
                            if (refreshedTask) {
                                // 如果 steps 為空或未載入，強制載入 steps
                                if (
                                    !Array.isArray(refreshedTask.steps) ||
                                    refreshedTask.steps.length === 0
                                ) {
                                    if (props.forceLoadTaskSteps) {
                                        await props.forceLoadTaskSteps(task.id);
                                        // 等待一下確保資料已更新
                                        await nextTick();
                                        // 重新取得更新後的資料
                                        const finalMission = props
                                            .getMissionHierarchyList()
                                            .find(
                                                (m) =>
                                                    normalizeNodeId(m.id) ===
                                                    normalizeNodeId(mission.id)
                                            );
                                        if (finalMission) {
                                            const finalChapter = (
                                                finalMission.chapters || []
                                            ).find(
                                                (ch) =>
                                                    normalizeNodeId(ch.id) ===
                                                    normalizeNodeId(chapter.id)
                                            );
                                            if (finalChapter) {
                                                const finalTask = (
                                                    finalChapter.tasks || []
                                                ).find(
                                                    (t) =>
                                                        normalizeNodeId(
                                                            t.id
                                                        ) ===
                                                        normalizeNodeId(task.id)
                                                );
                                                if (finalTask) {
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }

                    // 等待一下確保 computed 已更新
                    await nextTick();
                    return;
                }

                // 如果是 step 類型，但 task.steps 還沒有載入，先確保載入
                if (nodeType === 'step') {
                    // 確保該 task 的 steps 已經載入
                    if (!Array.isArray(task.steps) || task.steps.length === 0) {
                        // 先嘗試確保主線階層已載入（這會載入 task 的 steps）
                        await props.ensureMissionHierarchy(
                            normalizeNodeId(mission.id)
                        );
                        // 等待一下確保資料已更新
                        await nextTick();
                        // 重新取得 mission 資料（因為 ensureMissionHierarchy 會更新資料）
                        const updatedMission = props
                            .getMissionHierarchyList()
                            .find(
                                (m) =>
                                    normalizeNodeId(m.id) ===
                                    normalizeNodeId(mission.id)
                            );
                        if (updatedMission) {
                            const updatedChapter = (
                                updatedMission.chapters || []
                            ).find(
                                (ch) =>
                                    normalizeNodeId(ch.id) ===
                                    normalizeNodeId(chapter.id)
                            );
                            if (updatedChapter) {
                                // 更新 chapter.tasks，以便後續查找
                                if (Array.isArray(updatedChapter.tasks)) {
                                    chapter.tasks = updatedChapter.tasks;
                                }
                                const updatedTask = (
                                    updatedChapter.tasks || []
                                ).find(
                                    (t) =>
                                        normalizeNodeId(t.id) ===
                                        normalizeNodeId(task.id)
                                );
                                if (updatedTask) {
                                    // 更新 task.steps，以便後續查找
                                    task.steps = Array.isArray(
                                        updatedTask.steps
                                    )
                                        ? updatedTask.steps
                                        : [];

                                    // 如果 steps 還是為空，強制載入 steps
                                    if (
                                        (!Array.isArray(task.steps) ||
                                            task.steps.length === 0) &&
                                        props.forceLoadTaskSteps
                                    ) {
                                        await props.forceLoadTaskSteps(task.id);
                                        // 等待一下確保資料已更新
                                        await nextTick();
                                        // 重新取得更新後的資料
                                        const finalMission = props
                                            .getMissionHierarchyList()
                                            .find(
                                                (m) =>
                                                    normalizeNodeId(m.id) ===
                                                    normalizeNodeId(mission.id)
                                            );
                                        if (finalMission) {
                                            const finalChapter = (
                                                finalMission.chapters || []
                                            ).find(
                                                (ch) =>
                                                    normalizeNodeId(ch.id) ===
                                                    normalizeNodeId(chapter.id)
                                            );
                                            if (finalChapter) {
                                                const finalTask = (
                                                    finalChapter.tasks || []
                                                ).find(
                                                    (t) =>
                                                        normalizeNodeId(
                                                            t.id
                                                        ) ===
                                                        normalizeNodeId(task.id)
                                                );
                                                if (finalTask) {
                                                    task.steps = Array.isArray(
                                                        finalTask.steps
                                                    )
                                                        ? finalTask.steps
                                                        : [];
                                                }
                                            }
                                        }
                                    }

                                    // 在載入後的 steps 中查找
                                    if (Array.isArray(task.steps)) {
                                        for (const step of task.steps) {
                                            if (
                                                normalizeNodeId(step.id) ===
                                                normalizedNodeId
                                            ) {
                                                selectedNodePath.missionId =
                                                    normalizeNodeId(mission.id);
                                                selectedNodePath.chapterId =
                                                    normalizeNodeId(chapter.id);
                                                selectedNodePath.taskId =
                                                    normalizeNodeId(task.id);
                                                selectedNodePath.stepId =
                                                    normalizeNodeId(step.id);
                                                // 等待一下確保 computed 已更新
                                                await nextTick();
                                                return;
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    } else {
                        // steps 已經載入，直接查找
                        for (const step of task.steps) {
                            if (
                                nodeType === 'step' &&
                                normalizeNodeId(step.id) === normalizedNodeId
                            ) {
                                selectedNodePath.missionId = normalizeNodeId(
                                    mission.id
                                );
                                selectedNodePath.chapterId = normalizeNodeId(
                                    chapter.id
                                );
                                selectedNodePath.taskId = normalizeNodeId(
                                    task.id
                                );
                                selectedNodePath.stepId = normalizeNodeId(
                                    step.id
                                );
                                // 等待一下確保 computed 已更新
                                await nextTick();
                                return;
                            }
                        }
                    }
                }
            }
        }
    }
};

// 預載所有主線的階層資料
const preloadAllHierarchies = async () => {
    // 使用 getMissionHierarchyList() 獲取所有主線
    const missions = props.getMissionHierarchyList();
    await Promise.all(
        missions.map((m) => props.ensureMissionHierarchy(normalizeNodeId(m.id)))
    );
};

// 初始化選擇路徑
const initializePath = async () => {
    // 先清空所有值（使用 Object.assign 避免觸發 watch）
    Object.assign(selectedNodePath, {
        missionId: null,
        chapterId: null,
        taskId: null,
        stepId: null
    });

    // 清空初始節點名稱
    initialNodeName.value = null;

    if (props.mode === 'condition' && props.initialCode) {
        // 優先使用 getNodeHierarchy API 獲取完整路徑
        if (props.getNodeHierarchy) {
            try {
                // 從 code 中解析出 nodeId（例如：step_1027 -> 1027）
                const nodeId = getNodeIdFromCode(props.initialCode);
                if (nodeId) {
                    const response = await props.getNodeHierarchy(nodeId);
                    const hierarchyData =
                        response.data || response.result?.data || [];

                    if (
                        Array.isArray(hierarchyData) &&
                        hierarchyData.length > 0
                    ) {
                        // 從階層資料中提取各層級的 ID
                        let missionId = null;
                        let chapterId = null;
                        let taskId = null;
                        let stepId = null;
                        let targetNodeType = null; // 記錄目標節點類型（最後一個節點）

                        for (const node of hierarchyData) {
                            const nodeId = String(node.id); // 直接使用 API 回傳的 id
                            switch (node.node_type) {
                                case 'mission':
                                    missionId = nodeId;
                                    targetNodeType = 'mission';
                                    break;
                                case 'chapter':
                                    chapterId = nodeId;
                                    targetNodeType = 'chapter';
                                    break;
                                case 'task':
                                    taskId = nodeId;
                                    targetNodeType = 'task';
                                    break;
                                case 'step':
                                    stepId = nodeId;
                                    targetNodeType = 'step';
                                    break;
                            }
                        }

                        // 設定選擇的路徑（直接使用 API 回傳的資料，一次性設定避免 watch 清空）
                        Object.assign(selectedNodePath, {
                            missionId,
                            chapterId,
                            taskId,
                            stepId
                        });

                        // 根據 API 回傳的資料載入對應的階層資料（確保下拉選單有選項）
                        if (missionId) {
                            // 載入 mission 的完整階層（包括 chapters, tasks, steps）
                            await props.ensureMissionHierarchy(missionId);
                            await nextTick();

                            // 如果目標是 task 或 step，確保 steps 已載入（用於顯示下拉選單）
                            if (
                                (targetNodeType === 'task' ||
                                    targetNodeType === 'step') &&
                                taskId &&
                                props.forceLoadTaskSteps
                            ) {
                                await props.forceLoadTaskSteps(taskId);
                                await nextTick();
                            }
                        }

                        return; // 成功使用 API，直接返回
                    }
                }
            } catch (error) {
                console.error(
                    '[node-selector] getNodeHierarchy API failed for condition, falling back to parseNodeCodeToPath',
                    error
                );
                // API 失敗時，回退到原有的解析方式
            }
        }

        // 回退到原有的解析方式
        // 先把階層載好，避免 parseNodeCodeToPath 找不到 chapter / task / step
        await preloadAllHierarchies();
        await parseNodeCodeToPath(props.initialCode);
    } else if (props.mode !== 'condition' && props.initialNodeId) {
        // 優先使用 getNodeHierarchy API 獲取完整路徑
        if (props.getNodeHierarchy) {
            try {
                const response = await props.getNodeHierarchy(
                    props.initialNodeId
                );
                const hierarchyData =
                    response.data || response.result?.data || [];

                if (Array.isArray(hierarchyData) && hierarchyData.length > 0) {
                    // 從階層資料中提取各層級的 ID（直接使用 API 回傳的資料）
                    let missionId = null;
                    let chapterId = null;
                    let taskId = null;
                    let stepId = null;
                    let targetNodeType = null; // 記錄目標節點類型（最後一個節點）
                    let targetNodeName = null; // 記錄目標節點名稱

                    for (const node of hierarchyData) {
                        const nodeId = String(node.id); // 直接使用 API 回傳的 id
                        switch (node.node_type) {
                            case 'mission':
                                missionId = nodeId;
                                targetNodeType = 'mission';
                                break;
                            case 'chapter':
                                chapterId = nodeId;
                                targetNodeType = 'chapter';
                                break;
                            case 'task':
                                taskId = nodeId;
                                targetNodeType = 'task';
                                break;
                            case 'step':
                                stepId = nodeId;
                                targetNodeType = 'step';
                                break;
                        }
                    }

                    // 取得目標節點的名稱（最後一個節點）
                    const targetNode = hierarchyData[hierarchyData.length - 1];
                    targetNodeName =
                        targetNode.name ||
                        `未命名節點（ID: ${props.initialNodeId}）`;

                    // 設定選擇的路徑（直接使用 API 回傳的資料，一次性設定避免 watch 清空）
                    Object.assign(selectedNodePath, {
                        missionId,
                        chapterId,
                        taskId,
                        stepId
                    });

                    // 保存初始節點名稱（用於顯示和確認時返回）
                    initialNodeName.value = targetNodeName;

                    // 根據 API 回傳的資料載入對應的階層資料（確保下拉選單有選項）
                    if (missionId) {
                        // 載入 mission 的完整階層（包括 chapters, tasks, steps）
                        await props.ensureMissionHierarchy(missionId);
                        await nextTick();

                        // 如果目標是 task 或 step，確保 steps 已載入（用於顯示下拉選單）
                        if (
                            (targetNodeType === 'task' ||
                                targetNodeType === 'step') &&
                            taskId &&
                            props.forceLoadTaskSteps
                        ) {
                            await props.forceLoadTaskSteps(taskId);
                            await nextTick();
                        }
                    }

                    return; // 成功使用 API，直接返回
                }
            } catch (error) {
                console.error(
                    '[node-selector] getNodeHierarchy API failed, falling back to parseNodeCodeToPath',
                    error
                );
                // API 失敗時，回退到原有的解析方式
            }
        }

        // 回退到原有的解析方式
        await preloadAllHierarchies();
        // 等待一下確保階層資料已更新
        await nextTick();
        // 直接使用 parseNodeCodeToPath 來解析，它會自動處理載入邏輯
        // 先嘗試從 code 解析（根據 nodeType 推斷）
        const normalizedInitialId = normalizeNodeId(props.initialNodeId);
        let found = false;

        // 先嘗試查找 chapter
        for (const mission of props.getMissionHierarchyList()) {
            for (const chapter of mission.chapters || []) {
                if (normalizeNodeId(chapter.id) === normalizedInitialId) {
                    await parseNodeCodeToPath(`chapter_${props.initialNodeId}`);
                    found = true;
                    break;
                }
            }
            if (found) break;
        }

        // 如果沒找到 chapter，嘗試查找 task
        if (!found) {
            for (const mission of props.getMissionHierarchyList()) {
                // 確保章節已載入
                if (!mission.chapters || mission.chapters.length === 0) {
                    await props.ensureMissionHierarchy(
                        normalizeNodeId(mission.id)
                    );
                }
                for (const chapter of mission.chapters || []) {
                    // 確保任務已載入
                    if (
                        !Array.isArray(chapter.tasks) ||
                        chapter.tasks.length === 0
                    ) {
                        await props.ensureMissionHierarchy(
                            normalizeNodeId(mission.id)
                        );
                        // 重新取得資料
                        const updatedMission = props
                            .getMissionHierarchyList()
                            .find(
                                (m) =>
                                    normalizeNodeId(m.id) ===
                                    normalizeNodeId(mission.id)
                            );
                        if (updatedMission) {
                            const updatedChapter = (
                                updatedMission.chapters || []
                            ).find(
                                (ch) =>
                                    normalizeNodeId(ch.id) ===
                                    normalizeNodeId(chapter.id)
                            );
                            if (
                                updatedChapter &&
                                Array.isArray(updatedChapter.tasks)
                            ) {
                                chapter.tasks = updatedChapter.tasks;
                            }
                        }
                    }
                    for (const task of chapter.tasks || []) {
                        if (normalizeNodeId(task.id) === normalizedInitialId) {
                            await parseNodeCodeToPath(
                                `task_${props.initialNodeId}`
                            );
                            found = true;
                            break;
                        }
                    }
                    if (found) break;
                }
                if (found) break;
            }
        }

        // 如果沒找到 task，嘗試查找 step
        if (!found) {
            await parseNodeCodeToPath(`step_${props.initialNodeId}`);
            // 檢查是否成功解析
            if (selectedNodePath.stepId) {
                found = true;
            }
        }
    }

    // 多選模式：載入已選擇的節點
    if (
        props.multiple &&
        Array.isArray(props.initialNodeIds) &&
        props.initialNodeIds.length > 0
    ) {
        selectedNodes.value = [];

        // 使用 Promise.all 並行載入所有節點的階層資訊
        const loadPromises = props.initialNodeIds.map(async (nodeId) => {
            // 先嘗試從已載入的資料中查找
            let found = false;
            let nodeInfo = null;

            for (const mission of props.getMissionHierarchyList()) {
                for (const chapter of mission.chapters || []) {
                    if (chapter.id === nodeId) {
                        nodeInfo = {
                            code: `chapter_${nodeId}`,
                            path: {
                                missionId: normalizeNodeId(mission.id),
                                chapterId: normalizeNodeId(chapter.id),
                                taskId: null,
                                stepId: null
                            },
                            label: chapter.name
                        };
                        found = true;
                        break;
                    }
                    for (const task of chapter.tasks || []) {
                        if (task.id === nodeId) {
                            nodeInfo = {
                                code: `task_${nodeId}`,
                                path: {
                                    missionId: normalizeNodeId(mission.id),
                                    chapterId: normalizeNodeId(chapter.id),
                                    taskId: normalizeNodeId(task.id),
                                    stepId: null
                                },
                                label: task.name
                            };
                            found = true;
                            break;
                        }
                        for (const step of task.steps || []) {
                            if (step.id === nodeId) {
                                nodeInfo = {
                                    code: `step_${nodeId}`,
                                    path: {
                                        missionId: normalizeNodeId(mission.id),
                                        chapterId: normalizeNodeId(chapter.id),
                                        taskId: normalizeNodeId(task.id),
                                        stepId: normalizeNodeId(step.id)
                                    },
                                    label: step.name
                                };
                                found = true;
                                break;
                            }
                        }
                        if (found) break;
                    }
                    if (found) break;
                }
                if (found) break;
            }

            // 如果從已載入的資料中找不到，使用 getNodeHierarchy API
            if (!found && props.getNodeHierarchy) {
                try {
                    const response = await props.getNodeHierarchy(nodeId);
                    const hierarchyData =
                        response.data || response.result?.data || [];

                    if (
                        Array.isArray(hierarchyData) &&
                        hierarchyData.length > 0
                    ) {
                        // 從階層資料中提取各層級的 ID 和名稱
                        let missionId = null;
                        let chapterId = null;
                        let taskId = null;
                        let stepId = null;
                        let nodeName = null;
                        let nodeType = null;

                        // 最後一個節點是目標節點
                        const targetNode =
                            hierarchyData[hierarchyData.length - 1];
                        nodeName =
                            targetNode.name || `未命名節點（ID: ${nodeId}）`;
                        nodeType = targetNode.node_type;

                        for (const node of hierarchyData) {
                            const normalizedId = normalizeNodeId(node.id);
                            switch (node.node_type) {
                                case 'mission':
                                    missionId = normalizedId;
                                    break;
                                case 'chapter':
                                    chapterId = normalizedId;
                                    break;
                                case 'task':
                                    taskId = normalizedId;
                                    break;
                                case 'step':
                                    stepId = normalizedId;
                                    break;
                            }
                        }

                        // 根據節點類型生成 code
                        let code = '';
                        if (nodeType === 'chapter') {
                            code = `chapter_${nodeId}`;
                        } else if (nodeType === 'task') {
                            code = `task_${nodeId}`;
                        } else if (nodeType === 'step') {
                            code = `step_${nodeId}`;
                        } else if (nodeType === 'mission') {
                            code = `mission_${nodeId}`;
                        }

                        nodeInfo = {
                            code,
                            path: {
                                missionId,
                                chapterId,
                                taskId,
                                stepId
                            },
                            label: nodeName
                        };

                        // 確保相關的階層資料已載入（用於下拉選單顯示）
                        if (missionId) {
                            await props.ensureMissionHierarchy(missionId);
                            await nextTick();

                            // 如果是 task 或 step，確保 steps 已載入
                            if (
                                (nodeType === 'task' || nodeType === 'step') &&
                                taskId &&
                                props.forceLoadTaskSteps
                            ) {
                                // 如果是 step，使用 taskId；如果是 task，使用 nodeId
                                const targetTaskId =
                                    nodeType === 'step'
                                        ? Number(taskId)
                                        : Number(nodeId);
                                await props.forceLoadTaskSteps(targetTaskId);
                                await nextTick();
                            }
                        }
                    }
                } catch (error) {
                    console.error(
                        `[node-selector] 載入節點 ${nodeId} 的階層資訊失敗:`,
                        error
                    );
                    // 如果 API 失敗，至少建立一個基本的節點資訊
                    nodeInfo = {
                        code: `unknown_${nodeId}`,
                        path: {
                            missionId: null,
                            chapterId: null,
                            taskId: null,
                            stepId: null
                        },
                        label: `節點 ID: ${nodeId}`
                    };
                }
            }

            return nodeInfo;
        });

        // 等待所有節點資訊載入完成
        const nodeInfos = await Promise.all(loadPromises);

        // 過濾掉 null 值並加入到 selectedNodes
        selectedNodes.value = nodeInfos.filter((info) => info !== null);
    }
};

// 確認選擇
const handleConfirm = async () => {
    if (props.mode === 'condition') {
        const code = getNodeCodeFromPath();
        const nodeId = getNodeIdFromCode(code);
        let nodeName = getNodeLabelFromPath();

        // 如果找不到名稱，嘗試使用 API 取得
        if (!nodeName && nodeId && props.getNodeHierarchy) {
            try {
                const response = await props.getNodeHierarchy(nodeId);
                const hierarchyData =
                    response.data || response.result?.data || [];
                if (Array.isArray(hierarchyData) && hierarchyData.length > 0) {
                    const targetNode = hierarchyData[hierarchyData.length - 1];
                    nodeName = targetNode.name || `未命名節點（ID: ${nodeId}）`;
                }
            } catch (error) {
                console.error('[node-selector] 取得節點名稱失敗:', error);
            }
        }

        emit('confirm', { code, nodeId, nodeName });
    } else {
        const nodeCode = getNodeCodeFromPath();
        if (nodeCode) {
            const nodeId = getNodeIdFromCode(nodeCode);
            let nodeName = getNodeLabelFromPath(); // 獲取節點名稱

            // 如果找不到名稱，嘗試使用 API 取得
            if (!nodeName && nodeId && props.getNodeHierarchy) {
                try {
                    const response = await props.getNodeHierarchy(nodeId);
                    const hierarchyData =
                        response.data || response.result?.data || [];
                    if (
                        Array.isArray(hierarchyData) &&
                        hierarchyData.length > 0
                    ) {
                        const targetNode =
                            hierarchyData[hierarchyData.length - 1];
                        nodeName =
                            targetNode.name || `未命名節點（ID: ${nodeId}）`;
                    }
                } catch (error) {
                    console.error('[node-selector] 取得節點名稱失敗:', error);
                }
            }

            emit('confirm', { nodeId, nodeName }); // 返回節點名稱
        } else {
            console.warn(
                '[node-selector] handleConfirm: no nodeCode generated',
                selectedNodePath
            );
        }
    }
    localVisible.value = false;
};

// 加入選擇（多選模式）
const handleAddToSelection = () => {
    const nodeCode = getNodeCodeFromPath();
    if (nodeCode) {
        const finalNodeId = getNodeIdFromCode(nodeCode);
        const existingIndex = selectedNodes.value.findIndex((node) => {
            const existingNodeId = getNodeIdFromCode(node.code);
            return existingNodeId === finalNodeId;
        });
        if (existingIndex === -1) {
            selectedNodes.value.push({
                code: nodeCode,
                path: {
                    missionId: normalizeNodeId(selectedNodePath.missionId),
                    chapterId: normalizeNodeId(selectedNodePath.chapterId),
                    taskId: normalizeNodeId(selectedNodePath.taskId),
                    stepId: normalizeNodeId(selectedNodePath.stepId)
                },
                label: getNodeLabelFromPath()
            });
            // 重置路徑以便繼續選擇
            selectedNodePath.missionId = null;
            selectedNodePath.chapterId = null;
            selectedNodePath.taskId = null;
            selectedNodePath.stepId = null;
        } else {
            message.warning('該節點已選擇');
        }
    }
};

// 完成多選
const handleFinishMultiple = () => {
    const nodeIds = selectedNodes.value
        .map((node) => getNodeIdFromCode(node.code))
        .filter(Boolean);
    // 建立 ID 到名稱的映射
    const nodeNames = {};
    selectedNodes.value.forEach((node) => {
        const nodeId = getNodeIdFromCode(node.code);
        if (nodeId && node.label) {
            nodeNames[nodeId] = node.label;
        }
    });
    emit('confirm', { nodeIds, nodeNames });
    localVisible.value = false;
    selectedNodes.value = [];
};

// 移除已選擇的節點
const removeSelectedNode = (index) => {
    selectedNodes.value.splice(index, 1);
};

// 取消選擇
const handleCancel = () => {
    localVisible.value = false;
    selectedNodePath.missionId = null;
    selectedNodePath.chapterId = null;
    selectedNodePath.taskId = null;
    selectedNodePath.stepId = null;
    selectedNodes.value = [];
    initialNodeName.value = null;
};

// 章節改變時，清掉 task / step，並確保任務已載入
watch(
    () => selectedNodePath.chapterId,
    async (chapterId, oldChapterId) => {
        // 只有在 chapterId 真正改變時才清空（避免初始化時清空：oldChapterId 為 null 或 undefined 時不清空）
        if (
            chapterId !== oldChapterId &&
            oldChapterId !== undefined &&
            oldChapterId !== null
        ) {
            selectedNodePath.taskId = null;
            selectedNodePath.stepId = null;
        }

        // 確保該章節的任務已載入
        if (chapterId && selectedNodePath.missionId) {
            const missionId = normalizeNodeId(selectedNodePath.missionId);
            if (missionId !== null) {
                loadingTasks.value = true;
                try {
                    // 確保主線階層已載入（這會載入章節的任務）
                    await props.ensureMissionHierarchy(missionId);
                    // 等待資料更新
                    await nextTick();
                    // 如果章節的 tasks 還是空的，可能是該章節真的沒有任務，但我們需要確保資料已載入
                    const mission = props
                        .getMissionHierarchyList()
                        .find((m) => normalizeNodeId(m.id) === missionId);
                    if (mission) {
                        const chapter = (mission.chapters || []).find(
                            (ch) => normalizeNodeId(ch.id) === chapterId
                        );
                        // 如果找到章節但 tasks 不是陣列，表示資料還沒載入完成，再等待一下
                        if (chapter && !Array.isArray(chapter.tasks)) {
                            await nextTick();
                        }
                    }
                } finally {
                    loadingTasks.value = false;
                }
            }
        }
    }
);

// 任務改變時，清掉 step，並確保 steps 已載入
watch(
    () => selectedNodePath.taskId,
    async (taskId, oldTaskId) => {
        // 只有在 taskId 真正改變時才清空（避免初始化時清空：oldTaskId 為 null 或 undefined 時不清空）
        if (
            taskId !== oldTaskId &&
            oldTaskId !== undefined &&
            oldTaskId !== null
        ) {
            selectedNodePath.stepId = null;
        }

        // 確保該任務的 steps 已載入
        if (
            taskId &&
            selectedNodePath.missionId &&
            selectedNodePath.chapterId
        ) {
            const missionId = normalizeNodeId(selectedNodePath.missionId);
            if (missionId !== null) {
                loadingSteps.value = true;
                try {
                    // 確保主線階層已載入（這會載入任務的 steps）
                    await props.ensureMissionHierarchy(missionId);
                    // 等待資料更新
                    await nextTick();
                    // 如果任務的 steps 還是空的，可能需要強制載入
                    const mission = props
                        .getMissionHierarchyList()
                        .find((m) => normalizeNodeId(m.id) === missionId);
                    if (mission) {
                        const chapter = (mission.chapters || []).find(
                            (ch) =>
                                normalizeNodeId(ch.id) ===
                                normalizeNodeId(selectedNodePath.chapterId)
                        );
                        if (chapter) {
                            const task = (chapter.tasks || []).find(
                                (t) => normalizeNodeId(t.id) === taskId
                            );
                            // 如果找到任務但 steps 不是陣列，或 steps 為空但應該有資料，強制載入
                            if (
                                task &&
                                (!Array.isArray(task.steps) ||
                                    (task.steps.length === 0 &&
                                        props.forceLoadTaskSteps))
                            ) {
                                if (props.forceLoadTaskSteps) {
                                    await props.forceLoadTaskSteps(taskId);
                                    await nextTick();
                                }
                            }
                        }
                    }
                } finally {
                    loadingSteps.value = false;
                }
            }
        }
    }
);

// 監聽 visible 變化，初始化路徑
watch(
    () => props.visible,
    (newVal) => {
        if (newVal) {
            nextTick(() => {
                initializePath(); // initializePath 本身是 async 也沒關係
            });
        }
    },
    { immediate: true }
);

// 監聽主線選擇，確保階層資料載入
watch(
    () => normalizeNodeId(selectedNodePath.missionId),
    async (missionId) => {
        if (missionId !== null) {
            loadingChapters.value = true;
            try {
                await props.ensureMissionHierarchy(missionId);
                // 等待資料更新，確保 computed 能取得最新資料
                await nextTick();
            } finally {
                loadingChapters.value = false;
            }
        }
    }
);
</script>

<style scoped>
.node-selector-tip {
    margin-bottom: 20px;
}

.node-selector-tip :deep(.ant-alert) {
    background: linear-gradient(
        135deg,
        rgba(255, 158, 207, 0.08),
        rgba(42, 74, 122, 0.08)
    ) !important;
    border: 1px solid rgba(42, 74, 122, 0.3) !important;
    border-radius: 8px !important;
    padding: 12px 16px !important;
}

.node-selector-tip :deep(.ant-alert-message) {
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    line-height: 1.6 !important;
    margin: 0 !important;
}

.node-selector-tip :deep(.ant-alert-icon) {
    color: #2a4a7a !important;
}

.node-selector :deep(.ant-form-item) {
    margin-bottom: 0 !important;
}

.node-selector :deep(.ant-form-item-label) {
    padding-bottom: 4px !important;
}

.node-selector :deep(.ant-select) {
    height: 32px !important;
}

.node-selector :deep(.ant-select-selector) {
    height: 32px !important;
    display: flex !important;
    align-items: center !important;
}

.chapter-node-selector-footer {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.selected-nodes-display {
    background: linear-gradient(
        135deg,
        rgba(255, 158, 207, 0.08),
        rgba(42, 74, 122, 0.08)
    );
    border: 1px solid rgba(42, 74, 122, 0.3);
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 8px;
}

.selected-nodes-header {
    margin-bottom: 12px;
}

.selected-count {
    font-family: 'Courier New', monospace;
    font-size: 14px;
    font-weight: 600;
    color: #2a4a7a;
    display: flex;
    align-items: center;
}

.selected-count::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    background: linear-gradient(45deg, #64748b, #2a4a7a);
    border-radius: 50%;
    margin-right: 8px;
    box-shadow: 0 0 8px rgba(42, 74, 122, 0.5);
}

.selected-nodes-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.selected-node-tag {
    background: linear-gradient(
        45deg,
        rgba(42, 74, 122, 0.15),
        rgba(138, 175, 255, 0.15)
    ) !important;
    border: 1px solid rgba(42, 74, 122, 0.4) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
    padding: 4px 12px !important;
    border-radius: 16px !important;
    transition: all 0.3s ease !important;
    box-shadow: 0 2px 4px rgba(42, 74, 122, 0.2) !important;
}

.selected-node-tag:hover {
    background: linear-gradient(
        45deg,
        rgba(42, 74, 122, 0.25),
        rgba(138, 175, 255, 0.25)
    ) !important;
    border-color: rgba(42, 74, 122, 0.6) !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(42, 74, 122, 0.3) !important;
}

.selected-node-tag :deep(.anticon-close) {
    color: #8a8a9a !important;
    transition: color 0.2s ease !important;
}

.selected-node-tag :deep(.anticon-close):hover {
    color: #2a4a7a !important;
}

.footer-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 8px;
    border-top: 1px solid rgba(42, 74, 122, 0.2);
}

.selected-path {
    margin-top: 16px;
}

.path-label {
    font-family: 'Courier New', monospace;
    font-size: 14px;
    font-weight: 600;
    color: #2a2a3a;
    margin-bottom: 8px;
}

.path-content {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.debug-info {
    margin-top: 16px;
    padding: 12px;
    background: rgba(42, 74, 122, 0.05);
    border-radius: 8px;
    font-family: 'Courier New', monospace;
    font-size: 12px;
    color: #666;
}

.debug-pre {
    margin: 0;
    white-space: pre-wrap;
    word-break: break-all;
}

/* 確保 NodeSelectorModal 顯示在編輯視窗之上 */
:deep(.ant-modal) {
    z-index: 3000 !important;
}

:deep(.ant-modal-mask) {
    z-index: 2999 !important;
}

/* 確保 Modal 的 wrap 也有正確的 z-index */
:deep(.ant-modal-wrap) {
    z-index: 3000 !important;
}
</style>
